import {
  FilterSelector,
  type FilterOption,
  type FilterOptionItem,
} from "@/components/ui/FilterSelector/FilterSelector";
import {
  Button,
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui";
import { FilterBar } from "@/components/ui/FilterBar/FilterBar";
import { retrieveContactTagQueryOptions } from "@/lib/data/react-query";
import {
  CONTACT_TYPES,
  CONTACT_ORGANIZATION_SUBTYPES,
} from "@/lib/data/static/contactStatics";
import { US_STATES } from "@/lib/utils/states";
import { useQuery } from "@tanstack/react-query";
import { sortBy, startCase } from "lodash";
import { Check } from "lucide-react";
import { useMemo, useState } from "react";

interface ContactsSearchBarProps {
  searchObject: { [key: string]: string[] };
  addSearchObjectValue: (key: string, value: string) => void;
  removeSearchObjectValue: (key: string, value: string) => void;
  defaultSearchField?: string;
  fullWidth?: boolean;
  layout?: "selector" | "list" | "quick" | "bar";
}

export const simpleSelectFormFieldClasses =
  "w-[180px] rounded-bl-none rounded-tl-none border-l-transparent focus:border-l focus:border-accent-foreground focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0";

const textFilter = (placeholder: string): FilterOption["input"] => ({
  type: "text",
  placeholder,
});

const defaultSearchFieldMap: Record<string, string> = {
  emailAddress: "emailAddress",
  phoneNumber: "phoneNumber",
  faxNumber: "faxNumber",
  websiteAddress: "websiteAddress",
  tagId: "tagLabel",
};

type QuickContactFilterProps = {
  filterOption: FilterOption;
  selectedValues: string[];
  onAddFilter: (key: string, value: string) => void;
  onRemoveFilter: (key: string, value: string) => void;
};

function QuickContactFilter({
  filterOption,
  selectedValues,
  onAddFilter,
  onRemoveFilter,
}: QuickContactFilterProps) {
  const [open, setOpen] = useState(false);
  const input = filterOption.input;
  const items =
    input.type === "select" || input.type === "searchable-select"
      ? input.items
      : [];
  const isSearchable = input.type === "searchable-select";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="h-8 rounded-full px-3 text-xs font-medium"
        >
          {filterOption.label}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-0">
        <Command>
          {isSearchable ? (
            <CommandInput
              placeholder={`Search ${filterOption.label.toLowerCase()}...`}
            />
          ) : null}
          <CommandList>
            <CommandEmpty>No options found.</CommandEmpty>
            {items.map((item) => {
              const isSelected = selectedValues.includes(item.value);

              return (
                <CommandItem
                  key={item.value}
                  value={`${item.label} ${item.value}`}
                  onSelect={() => {
                    if (isSelected) {
                      onRemoveFilter(filterOption.value, item.value);
                    } else {
                      onAddFilter(filterOption.value, item.value);
                    }
                    setOpen(false);
                  }}
                >
                  <Check className={isSelected ? "opacity-100" : "opacity-0"} />
                  {item.label}
                </CommandItem>
              );
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

const ContactsSearchBar = ({
  searchObject,
  addSearchObjectValue,
  removeSearchObjectValue,
  defaultSearchField = "name",
  fullWidth = false,
  layout = "bar",
}: ContactsSearchBarProps) => {
  const canViewDetails = Boolean(true);
  const canViewAddresses = Boolean(true);
  const canViewEmails = Boolean(true);
  const canViewPhones = Boolean(true);
  const canViewFaxes = Boolean(true);
  const canViewWebsites = Boolean(true);
  const canViewTags = Boolean(true);

  const { data: contactTagsData } = useQuery({
    ...retrieveContactTagQueryOptions(),
    enabled: canViewTags,
  });

  const tagItems = useMemo<FilterOptionItem[]>(() => {
    return (contactTagsData?.data ?? [])
      .filter((tag) => Boolean(tag.label))
      .sort((a, b) => (a.label ?? "").localeCompare(b.label ?? ""))
      .map((tag) => ({ label: tag.label ?? "", value: tag.label ?? "" }));
  }, [contactTagsData?.data]);

  const filterOptions = useMemo<FilterOption[]>(() => {
    const options: FilterOption[] = [];

    if (canViewDetails) {
      options.push(
        { label: "Name", value: "name", input: textFilter("Search name") },
        { label: "Title", value: "title", input: textFilter("Search title") },
        {
          label: "Organization type",
          value: "organizationSubTypes",
          input: {
            type: "searchable-select",
            placeholder: "Select organization type",
            items: CONTACT_ORGANIZATION_SUBTYPES.map((value) => ({
              label: value,
              value,
            })),
            emptyMessage: "No organization types available.",
          },
        },
        {
          label: "Preferred contact method",
          value: "preferredContactMethod",
          input: textFilter("Search method"),
        },
        {
          label: "Type",
          value: "type",
          input: {
            type: "searchable-select",
            placeholder: "Select type",
            items: CONTACT_TYPES.map((value) => ({
              label: startCase(value),
              value,
            })),
            emptyMessage: "No types available.",
          },
        },
      );
    }

    if (canViewAddresses) {
      options.push(
        {
          label: "Address",
          value: "street1",
          input: textFilter("Search street"),
        },
        { label: "City", value: "city", input: textFilter("Search city") },
        {
          label: "State",
          value: "state",
          input: {
            type: "searchable-select",
            placeholder: "Select state",
            items: US_STATES.map((state) => ({ label: state, value: state })),
            emptyMessage: "No states available.",
          },
        },
        {
          label: "ZIP code",
          value: "zipCode",
          input: textFilter("Search zip code"),
        },
      );
    }
    if (canViewEmails)
      options.push({
        label: "Email",
        value: "emailAddress",
        input: textFilter("Search email"),
      });
    if (canViewPhones)
      options.push({
        label: "Phone",
        value: "phoneNumber",
        input: textFilter("Search phone"),
      });
    if (canViewFaxes)
      options.push({
        label: "Fax",
        value: "faxNumber",
        input: textFilter("Search fax"),
      });
    if (canViewWebsites)
      options.push({
        label: "Website",
        value: "websiteAddress",
        input: textFilter("Search website"),
      });
    if (canViewTags)
      options.push({
        label: "Tag",
        value: "tagLabel",
        input: tagItems.length
          ? {
              type: "searchable-select",
              placeholder: "Select tag",
              items: tagItems,
              emptyMessage: "No tags available.",
            }
          : textFilter("Search tag"),
      });

    const sortedOptions = sortBy(options, "label");
    const defaultField = defaultSearchField
      ? (defaultSearchFieldMap[defaultSearchField] ?? defaultSearchField)
      : undefined;
    const defaultIndex = sortedOptions.findIndex(
      (option) => option.value === defaultField,
    );
    if (defaultIndex > -1) {
      const [defaultOption] = sortedOptions.splice(defaultIndex, 1);
      return [defaultOption, ...sortedOptions];
    }
    return sortedOptions;
  }, [
    canViewAddresses,
    canViewDetails,
    canViewEmails,
    canViewFaxes,
    canViewPhones,
    canViewTags,
    canViewWebsites,
    defaultSearchField,
    tagItems,
  ]);

  if (layout === "list") {
    const hasActiveFilters = Object.values(searchObject).some(
      (values) => values.length > 0,
    );

    return (
      <div className="space-y-5">
        {hasActiveFilters ? (
          <FilterSelector
            filterOptions={filterOptions}
            selectedFilters={searchObject}
            onAddFilter={addSearchObjectValue}
            onRemoveFilter={removeSearchObjectValue}
            badgesOnly
          />
        ) : null}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          {filterOptions.map((filterOption) => {
            const values = searchObject[filterOption.value] ?? [];

            return (
              <FilterSelector
                key={filterOption.value}
                filterOptions={[filterOption]}
                selectedFilters={
                  values.length > 0 ? { [filterOption.value]: values } : {}
                }
                onAddFilter={addSearchObjectValue}
                onRemoveFilter={removeSearchObjectValue}
                showHelperText={false}
                selectStyle={null}
                fullWidth
                hidePrimarySelect
                showActiveFilterBadges={false}
              />
            );
          })}
        </div>
      </div>
    );
  }

  if (layout === "quick") {
    const quickFilterValues = ["tagLabel", "type", "state"];
    const quickFilterOptions = quickFilterValues
      .map((value) => filterOptions.find((option) => option.value === value))
      .filter((option): option is FilterOption => option !== undefined)
      .filter(
        (option) =>
          option.input.type === "select" ||
          option.input.type === "searchable-select",
      );

    return (
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        {quickFilterOptions.map((filterOption) => (
          <QuickContactFilter
            key={filterOption.value}
            filterOption={filterOption}
            selectedValues={searchObject[filterOption.value] ?? []}
            onAddFilter={addSearchObjectValue}
            onRemoveFilter={removeSearchObjectValue}
          />
        ))}
      </div>
    );
  }

  if (layout === "bar") {
    return (
      <FilterBar
        filterOptions={filterOptions}
        selectedFilters={searchObject}
        onAddFilter={addSearchObjectValue}
        onRemoveFilter={removeSearchObjectValue}
        quickFilterValues={["tagLabel", "type", "state"]}
      />
    );
  }

  return (
    <FilterSelector
      filterOptions={filterOptions}
      selectedFilters={searchObject}
      onAddFilter={addSearchObjectValue}
      onRemoveFilter={removeSearchObjectValue}
      showHelperText={false}
      selectStyle="mb-[-8px]"
      fullWidth={fullWidth}
      containerClassName={fullWidth ? "max-w-none" : undefined}
    />
  );
};

export default ContactsSearchBar;
